"use client";

import { Package, Search } from "lucide-react";
import RealProductCard from "@/components/RealProductCard";
import RecommendationSections from "@/components/RecommendationSections";
import { useTranslations } from "next-intl";
import type { Product } from "@/types";

interface DeliveryProductGridProps {
  products: Product[];
  locale: string;
  searchQuery?: string;
  /** 검색 모드: shipping(택배) 또는 product(상품 검색) */
  mode?: "shipping" | "product";
  /** 카테고리 ID (비슷한 상품 추천용) */
  categoryId?: string;
  /** 현재 검색 지역 (다른 지역 추천용) */
  currentLocation?: string;
  /** 건수 줄 오른쪽 끝에 놓을 정렬 트리거 */
  sortControl?: React.ReactNode;
  /** 택배 받을 나라(ISO2) — 제목을 "대한민국 전국 택배"처럼 나라 이름으로 */
  countryCode?: string | null;
}

export default function DeliveryProductGrid({
  products,
  locale,
  searchQuery,
  mode = "shipping",
  categoryId,
  currentLocation,
  sortControl,
  countryCode,
}: DeliveryProductGridProps) {
  const t = useTranslations("search");

  const isProductSearch = mode === "product";
  const Icon = isProductSearch ? Search : Package;
  const countryName = (() => {
    if (!countryCode) return null;
    try {
      return new Intl.DisplayNames([locale], { type: "region" }).of(countryCode) ?? null;
    } catch {
      return null;
    }
  })();

  return (
    <div className="bg-white min-h-[calc(100dvh-10rem)] max-w-7xl mx-auto sm:pt-0 pb-safe-nav">
      {/* Header(09-28) — 제목 한 줄 + 개수·안내와 정렬 한 줄. 아이콘 없이, 무엇을 보고 있는지
          (어느 나라 택배 / 어떤 검색어)를 제목에 싣는다. */}
      <div className="px-4 pt-4 sm:px-6 sm:pt-6 lg:px-8">
        <h1 className="text-lg font-bold text-gray-900 sm:text-[22px]">
          {isProductSearch
            ? t("productSearch.title", { query: searchQuery || "" })
            : countryName
              ? t("shipping.titleCountry", { country: countryName })
              : t("shipping.title")}
        </h1>
        <div className="mt-1.5 flex items-center justify-between gap-3">
          <p className="text-xs text-gray-500 sm:text-sm">
            {isProductSearch
              ? t("productSearch.meta", { count: products.length })
              : t("shipping.meta", { count: products.length })}
          </p>
          {sortControl}
        </div>
      </div>

      {/* Product Grid */}
      <div className="px-4 pb-4 pt-3 sm:px-6 sm:pb-6 sm:pt-4 lg:px-8">
        {products.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4 md:gap-6">
            {products.map((product) => (
              <div key={product.id}>
                <RealProductCard product={product} />
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 sm:py-16">
            <Icon className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-base sm:text-lg">
              {isProductSearch
                ? t("productSearch.noResults", { query: searchQuery || "" })
                : t("shipping.noResults")}
            </p>
            <p className="text-gray-400 text-xs sm:text-sm mt-2">
              {isProductSearch
                ? t("productSearch.noResultsHint")
                : t("shipping.noResultsHint")}
            </p>
          </div>
        )}
      </div>

      {/* Recommendation Sections */}
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        <RecommendationSections
          products={products}
          locale={locale}
          categoryId={categoryId}
          currentLocation={currentLocation}
          layout="horizontal"
        />
      </div>
    </div>
  );
}
